import { forwardRef, useEffect, useImperativeHandle } from "react";
import QuillEditor from "../../../components/CreateQuestion/Question/QuillEditor";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import LocationIconOutline from "../../../assets/svg/LocationIconOutline";
import { PencilLineIcon, SaveIcon } from "lucide-react";

const About = forwardRef(
  ({ isEditing, setIsEditing, handleSave, profileDetails }, ref) => {
    const { user } = useSelector((state) => state.auth);
    const {
      handleSubmit,
      control,
      setValue,
      formState: { errors, isDirty },
    } = useFormContext({
      defaultValues: {
        about: user?.about,
      },
    });

    useImperativeHandle(ref, () => handleSubmit(handleSave));

    useEffect(() => {
      if (isEditing) {
        setValue("about", user?.about ?? "");
      }
    }, [user?.about, isEditing]);

    return (
      <div className="bg-[#FAFBFC] rounded-md p-5 w-full flex flex-col gap-2">
        <div className="flex gap-4 w-full">
          <div className="flex flex-col gap-2.5 flex-1 bg-white border rounded-md font-montserrat text-charcoal font-medium text-xs leading-6 px-5 pt-4">
            <div className="flex items-center gap-2 font-bold text-charcoal text-xs">
              Professional Summary
              <button
                onClick={() =>
                  isEditing
                    ? handleSubmit(handleSave)()
                    : setIsEditing((prev) => !prev)
                }
                className="flex items-center justify-center hover:bg-white rounded-md h-5 w-5"
              >
                {isEditing ? (
                  <SaveIcon size={14} />
                ) : (
                  <PencilLineIcon size={14} />
                )}
              </button>
            </div>
            {isEditing ? (
              <Controller
                name={"about"}
                control={control}
                rules={{
                  required: "This field is required.",
                }}
                render={({ field }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <QuillEditor {...field} />
                      {errors["about"]?.message && (
                        <span className="whitespace-pre text-xs mt-2 text-orange-700">
                          {errors["about"]?.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            ) : (
              <span
                className="text-charcoal"
                dangerouslySetInnerHTML={{
                  __html: (
                    profileDetails?.candidateDetails?.about ||
                    user?.about ||
                    profileDetails?.jobData?.candidateDetails?.about ||
                    "-"
                  ).trim(),
                }}
              />
            )}
          </div>
          <div className="flex flex-col gap-4 w-[37%]">
            <div className="bg-white border rounded-md p-4 flex flex-col gap-2.5 h-[107px]">
              <span className="font-bold text-charcoal text-xs">
                Work Experience
              </span>
              <span className="text-xs text-charcoal font-medium">
                Strategic technology leader with over 10 years{" "}
              </span>
            </div>
            <div className="bg-white border rounded-md p-4 flex flex-col gap-2.5 h-[107px]">
              <span className="font-bold text-charcoal text-xs">Skills</span>
              <span className="text-xs text-charcoal font-medium">
                React, Node Js
              </span>
            </div>
            <div className="bg-white border rounded-md p-4 flex flex-col gap-2.5 h-[107px]">
              <span className="font-bold text-charcoal text-xs">
                Education & Certifications
              </span>
              <span className="text-xs text-charcoal font-medium">
                Masters Degree in Software Engineering
              </span>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div>
        {!isEditing ? (
          <div className="h-full flex flex-1 flex-col overflow-y-scroll no-scrollbar w-full pr-20 pt-5">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <LocationIconOutline className={"w-4 h-4"} fill="#6f6f6f" />
                <span className="font-medium text-xs text-light-black">
                  {profileDetails?.candidateDetails?.location ??
                    user?.location ??
                    profileDetails?.jobData?.candidateDetails?.location}
                </span>
              </div>
              <div
                className="font-montserrat text-charcoal font-medium text-xs leading-5 pl-1 mb-3"
                dangerouslySetInnerHTML={{
                  __html:
                    profileDetails?.candidateDetails?.about ??
                    user?.about ??
                    profileDetails?.jobData?.candidateDetails?.about ??
                    "-",
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="font-montserrat text-[#383838] font-bold text-sm mb-8">
              About
            </span>
            <div className="mr-4">
              <Controller
                name={"about"}
                control={control}
                rules={{
                  required: "This field is required.",
                }}
                render={({ field }) => {
                  return (
                    <div className="flex flex-col gap-1">
                      <QuillEditor {...field} />
                      {errors["about"]?.message && (
                        <span className="whitespace-pre text-xs mt-2 text-orange-700">
                          {errors["about"]?.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default About;
